import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Button } from '@mui/material';
import { Link } from 'react-router-dom';
import withWebsiteContent from '../../../common/withWebsiteContent';

/**
 * Pure Hero component — all content supplied via props.
 *
 * Expected website-component defaultProps shape (hero.json):
 * {
 *   "headline": "...",
 *   "subline":  "...",
 *   "ctaLabel": "Zarezerwuj",   // optional, defaults to "Zarezerwuj"
 *   "ctaUrl":   "/book"         // optional, defaults to "/book"
 * }
 *
 * The `images` prop is injected by withWebsiteComponent from the
 * component's images/ folder in MinIO.
 */
export const HeroPure = ({
  headline = '',
  subline = '',
  ctaLabel = 'Zarezerwuj',
  ctaUrl = '/book',
  images = [],
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (images.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [images.length]);

  return (
    <Box
      sx={{
        position: 'relative',
        height: '90vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        textAlign: 'center',
        overflow: 'hidden',
      }}
    >
      {/* Background: gradient fallback when no images */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          zIndex: 0,
        }}
      />

      {/* Background: image carousel with fade */}
      {images.map((url, index) => (
        <Box
          key={url}
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url(${url})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: index === currentIndex ? 1 : 0,
            transition: 'opacity 1s ease-in-out',
            zIndex: 1,
          }}
        />
      ))}

      {/* Overlay for readability */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.25)',
          zIndex: 2,
        }}
      />

      <Container sx={{ position: 'relative', zIndex: 3 }}>
        <Typography
          variant="h2"
          component="h1"
          sx={{
            fontWeight: 700,
            mb: 3,
            fontSize: { xs: '2.5rem', md: '4rem' },
          }}
        >
          {headline}
        </Typography>
        <Typography
          variant="h5"
          sx={{
            mb: 4,
            opacity: 0.9,
            fontSize: { xs: '1.1rem', md: '1.5rem' },
          }}
        >
          {subline}
        </Typography>
        <Button
          component={Link}
          to={ctaUrl}
          variant="contained"
          size="large"
          sx={{
            px: 4,
            py: 1.5,
            fontSize: '1.1rem',
            backgroundColor: 'white',
            color: 'primary.main',
            '&:hover': {
              backgroundColor: 'grey.100',
            },
          }}
        >
          {ctaLabel}
        </Button>
      </Container>
    </Box>
  );
};

const Hero = withWebsiteContent(HeroPure);
Hero.displayName = 'Hero';

export default Hero;
